import { Dimensions, PixelRatio, Platform } from "react-native";

const PIXEL_RATIO = PixelRatio.get();
const SCREEN_WIDTH = Dimensions.get("window").width;

export const ImageSizePreset = {
  GRID: "grid",
  LIST_THUMB: "list_thumb",
  PDP_MAIN: "pdp_main",
  PDP_THUMB: "pdp_thumb",
  PDP_STICKY: "pdp_sticky",
  AD_BANNER: "ad_banner",
  SHARE_IMAGE: "share_image",
} as const;

type ImageSizePresetType =
  (typeof ImageSizePreset)[keyof typeof ImageSizePreset];

interface ImageDimensions {
  width: number;
  height: number;
}

const PRESET_DIMENSIONS: Record<ImageSizePresetType, ImageDimensions> = {
  [ImageSizePreset.GRID]: { width: 200, height: 200 },
  [ImageSizePreset.LIST_THUMB]: { width: 48, height: 48 },
  [ImageSizePreset.PDP_MAIN]: {
    width: Math.min(SCREEN_WIDTH, 400),
    height: Math.min(SCREEN_WIDTH, 400),
  },
  [ImageSizePreset.PDP_THUMB]: { width: 60, height: 60 },
  // Matches the 250dp × 250dp sticky-header carousel display size.
  // PDP_THUMB (60dp) was too small — Glide decoded at 120px and upscaled to
  // 500–750px on Android, causing blur that iOS (SDWebImage) doesn't show.
  [ImageSizePreset.PDP_STICKY]: { width: 250, height: 250 },
  [ImageSizePreset.AD_BANNER]: {
    width: Math.min(SCREEN_WIDTH, 600),
    height: 160,
  },
  [ImageSizePreset.SHARE_IMAGE]: { width: 140, height: 140 },
};

/**
 * Returns pixel dimensions for a given display-size preset.
 * Caps the retina multiplier at 2x to prevent excessive decode memory on 3x screens.
 * A 200pt grid cell on a 3x device would request 600px — that's 1.44MB per image.
 * Capping at 2x (400px) costs ~640KB and is visually indistinguishable at grid thumbnail sizes.
 */
export function getPixelDimensions(
  preset: ImageSizePresetType
): ImageDimensions {
  const cappedRatio = Math.min(PIXEL_RATIO, 2);
  const base = PRESET_DIMENSIONS[preset];
  return {
    width: Math.round(base.width * cappedRatio),
    height: Math.round(base.height * cappedRatio),
  };
}

/**
 * Transforms an image URL to request a server-resized version.
 *
 * Supports common CDN patterns:
 * - Cloudinary (res.cloudinary.com)
 * - imgix (*.imgix.net)
 * - Generic query-param based resizing (?width=X&height=Y)
 *
 * If the URL doesn't match a known CDN, appends generic query params
 * that most image proxy/CDN setups will understand.
 *
 * Returns the original URL unchanged on web (browser handles scaling)
 * or if the URL is empty/invalid.
 */
export function getOptimizedImageUrl(
  url: string | undefined | null,
  preset: ImageSizePresetType
): string {
  if (!url || url.length === 0) return "";
  if (Platform.OS === "web") return url;

  const dims = getPixelDimensions(preset);

  try {
    if (url.includes("res.cloudinary.com")) {
      return applyCloudinaryTransform(url, dims);
    }
    if (url.includes(".imgix.net")) {
      return applyImgixTransform(url, dims);
    }
    return applyGenericTransform(url, dims);
  } catch {
    return url;
  }
}

function applyCloudinaryTransform(url: string, dims: ImageDimensions): string {
  const uploadSegment = "/upload/";
  const idx = url.indexOf(uploadSegment);
  if (idx === -1) return url;

  const transform = `w_${dims.width},h_${dims.height},c_limit,q_auto,f_auto`;
  const insertPos = idx + uploadSegment.length;

  const afterUpload = url.substring(insertPos);
  if (/^[a-z]_/.test(afterUpload)) {
    const nextSlash = afterUpload.indexOf("/");
    if (nextSlash !== -1) {
      return (
        url.substring(0, insertPos) +
        transform +
        "/" +
        afterUpload.substring(nextSlash + 1)
      );
    }
  }

  return url.substring(0, insertPos) + transform + "/" + afterUpload;
}

function applyImgixTransform(url: string, dims: ImageDimensions): string {
  const separator = url.includes("?") ? "&" : "?";
  return `${url}${separator}w=${dims.width}&h=${dims.height}&fit=clip&auto=format,compress&q=75`;
}

function applyGenericTransform(url: string, dims: ImageDimensions): string {
  const separator = url.includes("?") ? "&" : "?";
  return `${url}${separator}width=${dims.width}&height=${dims.height}`;
}

/**
 * Returns expo-image source object with decode-size hints for both iOS and Android.
 *
 * iOS: hints tell SDWebImage the target decode size, enabling hardware-accelerated
 * downsampling during JPEG decode (via ImageIO).
 *
 * Android: hints are forwarded to Glide's .override(width, height), which caps the
 * decoded Bitmap size. Without hints, Glide decodes at full source resolution —
 * a 800×800 JPEG becomes ~2.56 MB in ARGB_8888 even when displayed at 200dp.
 * Passing explicit dimensions reduces per-image heap cost by 4–16×.
 */
export function getImageSource(
  uri: string,
  preset: ImageSizePresetType
): { uri: string; width?: number; height?: number } {
  const finalUri = normalizeGoogleDriveImageUrl(uri);

  if (Platform.OS === "web") {
    return { uri: finalUri || "" };
  }

  const dims = getPixelDimensions(preset);

  return {
    uri: finalUri || "",
    width: dims.width,
    height: dims.height,
  };
}

/**
 * expo-image props optimized for native memory on both iOS and Android.
 * These should be spread onto every ExpoImage in scrolling lists.
 *
 * - cachePolicy "disk": keeps decoded bitmaps off the memory cache, letting the
 *   OS reclaim them under pressure. On Android this prevents Glide's BitmapPool
 *   from retaining large bitmaps beyond the visible viewport.
 * - allowDownscaling true: permits the image decoder to subsample the source
 *   down to the rendered size. On Android this pairs with the width/height hints
 *   from getImageSource() to activate Glide's inSampleSize path.
 */
export function getImageMemoryProps() {
  if (Platform.OS === "web") return {};
  return {
    cachePolicy: "disk" as const,
    allowDownscaling: true,
  };
}

/**
 * @deprecated Use getImageMemoryProps() — now applies to both iOS and Android.
 */
export const getIOSImageProps = getImageMemoryProps;
function extractDriveFileId(url: string) {
  if (!url) return null;

  const patterns = [
    /\/d\/([a-zA-Z0-9_-]+)/,
    /[?&]id=([a-zA-Z0-9_-]+)/,
    /\/uc\?id=([a-zA-Z0-9_-]+)/,
  ];

  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match?.[1]) return match[1];
  }

  return null;
}

function toLH3Url(url: string): string | null {
  const id = extractDriveFileId(url);
  return id ? `https://lh3.googleusercontent.com/d/${id}` : null;
}

function normalizeGoogleDriveImageUrl(url: string): string {
  if (!isGoogleDriveUrl(url)) return url;
  return toLH3Url(url) || url;
}

function isGoogleDriveUrl(url: string) {
  if (!url) return false;
  return url.includes("drive.google.com");
}

export function toDrivePreviewUrl(url: string) {
  if (!isGoogleDriveUrl(url)) return url;

  const fileId = extractDriveFileId(url);
  if (!fileId) return url;

  return `https://drive.google.com/file/d/${fileId}/preview`;
}
